import { SSRExchange } from 'next-urql';
import env from 'app/env';

import {
    dedupExchange,
    fetchExchange,
    cacheExchange,
    errorExchange,
    ClientOptions,
    CombinedError,
    Operation,
} from 'urql';

import { persistedFetchExchange } from '@urql/exchange-persisted-fetch';
import { requestPolicyExchange } from '@urql/exchange-request-policy';
import Debug from 'debug';
import * as Sentry from '@sentry/browser';

const errorLogger = Debug('play:urql:error');

const CONTENTO_API_URL = `${env.CONTENTO_API}`;

const fetchOptions = {
    headers: {
        'User-Agent': env.USER_AGENT,
    },
};

const extraSettings = typeof window === 'undefined' ? fetchOptions : {};

const urqlFetch = (input: RequestInfo, init?: RequestInit) => {
    let newInit = init;
    // Fetch fails in iOS 10.??/Safari 10 if headers is undefined
    // https://bugs.webkit.org/show_bug.cgi?id=169970
    // https://bugs.webkit.org/show_bug.cgi?id=168043
    if (init && init.hasOwnProperty('headers') && typeof init.headers === 'undefined') {
        const { headers, ...allExceptHeaders } = init; // eslint-disable-line
        newInit = allExceptHeaders;
    }

    if (typeof input === 'string') {
        if (input.includes('?')) {
            input += `&ua=${env.USER_AGENT}-client`;
        } else {
            input += `?ua=${env.USER_AGENT}-client`;
        }
    }

    return fetch(input, newInit);
};

const handleError = (error: CombinedError, operation: Operation) => {
    const queryName = (operation as any).query.definitions[0].name?.value || 'Unknown';

    errorLogger(
        'Query %s returned error : graphQLErrors=%s networkError=%s message=%s',
        queryName,
        error.graphQLErrors.join(', '),
        error.networkError,
        error.message
    );

    if (typeof window !== 'undefined') {
        Sentry.captureException(error);
    }
};

const config: ClientOptions = {
    url: CONTENTO_API_URL,
    fetchOptions: extraSettings,
    fetch: urqlFetch,
    preferGetMethod: true,
    exchanges: [
        dedupExchange,
        errorExchange({
            onError: handleError,
        }),
        cacheExchange,
        persistedFetchExchange({}),
        fetchExchange,
    ],
};

export const withUrqlSsr = (
    ssrExchange: SSRExchange
    // ctx?: NextUrqlContext
): ClientOptions => {
    return {
        ...config,
        exchanges: [
            dedupExchange,
            errorExchange({
                onError: handleError,
            }),
            requestPolicyExchange({
                ttl: 30 * 1000,
            }),
            cacheExchange,
            ssrExchange, // order matters
            persistedFetchExchange(),
            fetchExchange,
        ],
    };
};

export const urqlConfig = config;
